import React from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { Music2, Library, Calendar, LogOut, Users, Building2, BarChart3, FileStack, Mic, Sun, Moon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useBusiness } from '../context/BusinessContext';
import { useTheme } from '../context/ThemeContext';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const Layout = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const { businesses, selectedBusinessId, selectedBusiness, selectBusiness, isSuperAdmin } = useBusiness();
  const { theme, toggleTheme, isLight } = useTheme();

  const handleLogout = () => {
    onLogout();
    navigate('/auth');
  };

  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';

  const navLinks = [
    { to: '/dashboard', icon: Music2, label: 'Dashboard', showFor: 'all' },
    { to: '/music', icon: Library, label: 'Music Library', showFor: 'admin' },
    { to: '/playlists', icon: Calendar, label: 'Playlists', showFor: 'admin' },
    { to: '/playlist-templates', icon: FileStack, label: 'Templates', showFor: 'super_admin' },
    { to: '/text-to-voice', icon: Mic, label: 'Text to Voice', showFor: 'super_admin' },
    { to: '/users', icon: Users, label: 'User Management', showFor: 'super_admin' },
    { to: '/businesses', icon: Building2, label: 'Businesses', showFor: 'super_admin' },
    { to: '/reports', icon: BarChart3, label: 'Reports', showFor: 'admin' },
  ];

  const visibleNavLinks = navLinks.filter(link => {
    if (link.showFor === 'all') return true;
    if (link.showFor === 'admin') return isAdmin;
    if (link.showFor === 'super_admin') return isSuperAdmin;
    return false;
  });

  return (
    <div className={`flex h-screen overflow-hidden ${isLight ? 'bg-[#faf8f5]' : 'bg-[#050508]'}`}>
      {/* Sidebar */}
      <aside className={`w-72 flex flex-col border-r ${
        isLight 
          ? 'bg-[#fffbf5] border-[#ebe7e0]' 
          : 'glass-card border-white/5'
      }`}>
        {/* Logo Section */}
        <div className="p-6">
          <div className="flex items-center justify-center mb-6">
            <div className="relative">
              {!isLight && <div className="absolute inset-0 bg-red-600/20 blur-3xl rounded-full scale-150"></div>}
              <img 
                src="/redmlabs-logo.png"
                alt="Redmlabs"
                className={`h-24 w-auto object-contain rounded-xl relative z-10 ${isLight ? '' : ''}`}
              />
            </div>
          </div>
          
          {/* User Card */}
          <div className={`rounded-xl p-4 space-y-3 ${
            isLight 
              ? 'bg-white border border-[#ebe7e0]' 
              : 'glass'
          }`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold text-sm ${
                  isLight 
                    ? 'bg-gradient-to-br from-[#e94560] to-[#ff6b6b]' 
                    : 'bg-gradient-to-br from-violet-600 to-cyan-500'
                }`}>
                  {(user.display_name || user.username || '?').charAt(0).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-medium truncate ${isLight ? 'text-gray-800' : 'text-white'}`}>
                    {user.display_name || user.username}
                  </p>
                  <p className={`text-xs ${isLight ? 'text-gray-500' : 'text-slate-500'}`}>
                    {isSuperAdmin ? 'Super Administrator' : user?.role === 'admin' ? 'Administrator' : 'User'}
                  </p>
                </div>
              </div>
            </div>
            
            {/* Role Badge */}
            <div className="flex">
              {isSuperAdmin && (
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                  isLight 
                    ? 'bg-[#e94560]/10 border border-[#e94560]/30 text-[#e94560]' 
                    : 'bg-gradient-to-r from-amber-600/20 to-orange-600/20 border border-amber-500/30 text-amber-400'
                }`}>
                  Super Admin
                </span>
              )}
              {user?.role === 'admin' && !isSuperAdmin && (
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                  isLight 
                    ? 'bg-[#e94560]/10 border border-[#e94560]/30 text-[#e94560]' 
                    : 'bg-violet-600/20 border border-violet-500/30 text-violet-400'
                }`}>
                  Admin
                </span>
              )}
              {user?.role === 'user' && (
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                  isLight 
                    ? 'bg-emerald-500/10 border border-emerald-500/30 text-emerald-600' 
                    : 'bg-emerald-600/20 border border-emerald-500/30 text-emerald-400'
                }`}>
                  User
                </span>
              )}
            </div>
          </div>
          
          {/* Business Selector for Super Admin */}
          {isSuperAdmin && businesses.length > 0 && (
            <div className="mt-4">
              <label className={`text-xs font-medium mb-2 block uppercase tracking-wider ${
                isLight ? 'text-gray-500' : 'text-slate-500'
              }`}>Viewing Business</label>
              <Select value={selectedBusinessId || ''} onValueChange={selectBusiness}>
                <SelectTrigger className={`w-full h-11 rounded-xl transition-colors ${
                  isLight 
                    ? 'bg-white border-[#ebe7e0] text-gray-800 hover:border-[#e94560]/50' 
                    : 'glass border-white/10 text-white hover:border-violet-500/50'
                }`}>
                  <SelectValue placeholder="Select business">
                    <span className="flex items-center gap-2">
                      <Building2 className={`w-4 h-4 ${isLight ? 'text-[#e94560]' : 'text-violet-400'}`} />
                      <span className="truncate">{selectedBusiness?.name || 'Select business'}</span>
                    </span>
                  </SelectValue>
                </SelectTrigger>
                <SelectContent className={`rounded-xl ${
                  isLight 
                    ? 'bg-white border-[#ebe7e0] text-gray-800' 
                    : 'glass-card border-white/10 text-white'
                }`}>
                  {businesses.map((business) => (
                    <SelectItem 
                      key={business.id} 
                      value={business.id}
                      className={`rounded-lg ${
                        isLight 
                          ? 'focus:bg-[#e94560]/10 focus:text-[#e94560]' 
                          : 'focus:bg-violet-600/20 focus:text-white'
                      }`}
                    >
                      {business.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-4 py-2 space-y-1">
          <p className={`text-xs font-medium uppercase tracking-wider px-4 mb-3 ${
            isLight ? 'text-gray-400' : 'text-slate-600'
          }`}>Navigation</p>
          {visibleNavLinks.map(({ to, icon: Icon, label }) => (
            <NavLink
              key={to}
              to={to}
              data-testid={`nav-${label.toLowerCase().replace(' ', '-')}`}
              className={({ isActive }) =>
                `group flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 ${
                  isActive
                    ? isLight
                      ? 'bg-[#e94560] text-white shadow-lg shadow-[#e94560]/25'
                      : 'bg-gradient-to-r from-violet-600 to-violet-700 text-white shadow-lg shadow-violet-600/25'
                    : isLight
                      ? 'text-gray-600 hover:text-[#e94560] hover:bg-[#e94560]/5'
                      : 'text-slate-400 hover:text-white hover:bg-white/5'
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <Icon className={`w-5 h-5 transition-transform group-hover:scale-110 ${
                    isActive 
                      ? 'text-white' 
                      : isLight 
                        ? 'text-gray-400 group-hover:text-[#e94560]' 
                        : 'text-slate-500 group-hover:text-violet-400'
                  }`} />
                  <span className="font-medium">{label}</span>
                  {isActive && (
                    <div className="ml-auto w-1.5 h-1.5 rounded-full bg-white animate-pulse"></div>
                  )}
                </>
              )}
            </NavLink>
          ))}
        </nav>

        {/* User role info */}
        {!isAdmin && (
          <div className={`mx-4 mb-4 p-4 rounded-xl ${
            isLight 
              ? 'bg-white border border-[#ebe7e0]' 
              : 'glass border border-white/5'
          }`}>
            <p className={`text-xs leading-relaxed ${isLight ? 'text-gray-500' : 'text-slate-400'}`}>
              You have <span className={`font-medium ${isLight ? 'text-emerald-600' : 'text-emerald-400'}`}>User</span> access.
              Contact an admin for more permissions.
            </p>
          </div>
        )}

        {/* Theme Toggle & Logout */}
        <div className={`p-4 border-t ${isLight ? 'border-[#ebe7e0]' : 'border-white/5'}`}>
          {/* Theme Toggle Button */}
          <Button
            onClick={toggleTheme}
            variant="ghost"
            className={`w-full justify-start rounded-xl py-3 mb-2 transition-all ${
              isLight 
                ? 'text-gray-600 hover:text-[#e94560] hover:bg-[#e94560]/5' 
                : 'text-slate-400 hover:text-white hover:bg-white/5'
            }`}
          >
            {isLight ? (
              <>
                <Moon className="w-5 h-5 mr-3" />
                <span className="font-medium">Dark Mode</span>
              </>
            ) : (
              <>
                <Sun className="w-5 h-5 mr-3" />
                <span className="font-medium">Light Mode</span>
              </>
            )}
          </Button>

          <Button
            onClick={handleLogout}
            data-testid="logout-button"
            variant="ghost"
            className={`w-full justify-start rounded-xl py-3 transition-all ${
              isLight 
                ? 'text-gray-600 hover:text-red-500 hover:bg-red-50' 
                : 'text-slate-400 hover:text-white hover:bg-red-600/10 hover:text-red-400'
            }`}
          >
            <LogOut className="w-5 h-5 mr-3" />
            <span className="font-medium">Logout</span>
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className={`flex-1 overflow-auto ${isLight ? 'bg-[#faf8f5]' : 'bg-[#050508]'}`}>
        <Outlet />
      </main>
    </div>
  );
};

export default Layout;
